import { FC, useCallback, useMemo, useState } from 'react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { TPgAirwallexAccount, TPgLegalEntity } from 'services/api/paymentGateway/types';
import { EasTable } from 'ui/EasTable';
import { ETableStructureAlign, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import { PgPage } from '../common/PgPage';
import { PgPanel } from '../common/PgPanel';
import { usePgAirwallexAccounts, usePgLegalEntities } from '../common/hooks';
import { PG_SETTINGS_GROUP } from '../constants';
import { LegalEntityDialog } from './LegalEntityDialog';
import { AirwallexAccountDialog } from './AirwallexAccountDialog';
import { AirwallexAccountCard } from './AirwallexAccountCard';

const EDIT_ACTION = 'Edit';

const column = (
	name: string,
	selector: keyof TPgLegalEntity,
	extra: Partial<TTableStructure<keyof TPgLegalEntity, TPgLegalEntity>> = {},
): TTableStructure<keyof TPgLegalEntity, TPgLegalEntity> => ({
	name,
	selector,
	show: true,
	sortable: false,
	filterable: false,
	type: ETableStructureType.STRING,
	align: ETableStructureAlign.LEFT,
	...extra,
});

/** Settings → Legal entities & Airwallex accounts (03 §4.4). */
export const Accounts: FC = () => {
	const { legalEntities, loading: legalEntitiesLoading } = usePgLegalEntities();
	const { accounts, loading: accountsLoading } = usePgAirwallexAccounts();

	const [legalEntityDialogOpen, setLegalEntityDialogOpen] = useState(false);
	const [legalEntityToEdit, setLegalEntityToEdit] = useState<TPgLegalEntity | null>(null);
	const [accountToEdit, setAccountToEdit] = useState<TPgAirwallexAccount | null>(null);

	const tableStructure = useMemo<TTableStructure<keyof TPgLegalEntity, TPgLegalEntity>[]>(() => {
		const accountName = (id: number) => {
			const account = accounts?.find((a) => a.id === id);
			return account ? `${account.displayName} (${account.code})` : `#${id}`;
		};
		return [
			column('Code', 'code'),
			column('Short name', 'name'),
			column('Legal name', 'legalName', { tooltip: (item) => item.address ?? '' }),
			column('Country', 'countryCode'),
			column('Registration no.', 'registrationNumber'),
			column('VAT no.', 'vatNumber'),
			column('Support e-mail', 'supportEmail'),
			column('Airwallex account', 'airwallexAccountId', {
				value: (item) => accountName(item.airwallexAccountId),
			}),
			column('Default', 'isDefault', {
				type: ETableStructureType.BOOLEAN,
				align: ETableStructureAlign.CENTER,
			}),
			column('Active', 'isActive', {
				type: ETableStructureType.BOOLEAN,
				align: ETableStructureAlign.CENTER,
			}),
			{
				name: 'Actions',
				selector: 'actions',
				noName: true,
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.BUTTONS,
				align: ETableStructureAlign.RIGHT,
				buttons: [{ name: EDIT_ACTION, icon: EIconButtons.EDIT }],
			},
		];
	}, [accounts]);

	const handleAddLegalEntity = useCallback(() => {
		setLegalEntityToEdit(null);
		setLegalEntityDialogOpen(true);
	}, []);

	const handleLegalEntityAction = useCallback(
		(id: number, action: string) => {
			if (action !== EDIT_ACTION) return;
			setLegalEntityToEdit(legalEntities?.find((entity) => entity.id === id) ?? null);
			setLegalEntityDialogOpen(true);
		},
		[legalEntities],
	);

	const handleLegalEntityDialogClose = useCallback(() => {
		setLegalEntityDialogOpen(false);
		setLegalEntityToEdit(null);
	}, []);

	return (
		<PgPage title="Legal Entities & Accounts" group={PG_SETTINGS_GROUP}>
			<PgPanel
				title="Legal entities"
				bodyPadding={0}
				actions={
					<>
						{legalEntitiesLoading && <CircularProgress size={18} />}
						<Button
							variant="contained"
							size="small"
							onClick={handleAddLegalEntity}
							disabled={!accounts?.length}
						>
							Add legal entity
						</Button>
					</>
				}
			>
				<EasTable<TPgLegalEntity>
					tableStructure={tableStructure}
					items={legalEntities ?? []}
					itemsIsLoading={legalEntitiesLoading && !legalEntities}
					handleAction={handleLegalEntityAction}
					skeletonRows={Array.from(new Array(3))}
				/>
			</PgPanel>

			<PgPanel
				title="Airwallex accounts"
				actions={accountsLoading ? <CircularProgress size={18} /> : undefined}
			>
				<Typography variant="body2" color="text.secondary">
					Accounts and their API keys come from the gateway configuration; here you can rename them,
					switch the default one, check the key and synchronize the webhook subscription and the
					Apple Pay domains.
				</Typography>
			</PgPanel>
			{(accounts ?? []).map((account) => (
				<AirwallexAccountCard key={account.id} account={account} onEdit={setAccountToEdit} />
			))}

			<LegalEntityDialog
				open={legalEntityDialogOpen}
				onClose={handleLegalEntityDialogClose}
				legalEntityToEdit={legalEntityToEdit}
				accounts={accounts ?? []}
			/>
			<AirwallexAccountDialog
				open={!!accountToEdit}
				onClose={() => setAccountToEdit(null)}
				account={accountToEdit}
			/>
		</PgPage>
	);
};
